import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { advisorService } from "../services/advisorService";
import { AdvisorCaseloadQuery, AdvisorCaseloadResponse } from "../types/advisor.types";

interface UseAdvisorDashboardResult {
  caseload: AdvisorCaseloadResponse | null;
  query: Required<
    Pick<
      AdvisorCaseloadQuery,
      "lead_limit" | "lead_offset" | "case_limit" | "case_offset" | "include_closed_cases"
    >
  > &
    Pick<AdvisorCaseloadQuery, "q" | "visa_type">;
  loading: boolean;
  error: string | null;
  refreshing: boolean;
  setSearch: (value: string) => void;
  setVisaType: (value: string) => void;
  toggleIncludeClosedCases: () => void;
  nextLeadPage: () => void;
  prevLeadPage: () => void;
  nextCasePage: () => void;
  prevCasePage: () => void;
  refresh: () => Promise<void>;
}

type AdvisorQueryState = UseAdvisorDashboardResult["query"];

const defaultQuery: AdvisorQueryState = {
  lead_limit: 5,
  lead_offset: 0,
  case_limit: 8,
  case_offset: 0,
  include_closed_cases: false,
  q: "",
  visa_type: "",
};

const toErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
};

export const useAdvisorDashboard = (): UseAdvisorDashboardResult => {
  const { user } = useAuth();
  const [caseload, setCaseload] = useState<AdvisorCaseloadResponse | null>(null);
  const [query, setQuery] = useState<AdvisorQueryState>(defaultQuery);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setError("Advisor session is missing.");
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const data = await advisorService.getCaseload({
        lead_limit: query.lead_limit,
        lead_offset: query.lead_offset,
        case_limit: query.case_limit,
        case_offset: query.case_offset,
        include_closed_cases: query.include_closed_cases,
        q: query.q,
        visa_type: query.visa_type,
      });
      setCaseload(data);
    } catch (err) {
      setError(toErrorMessage(err, "Could not load advisor caseload."));
    }
  }, [query, user]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const setSearch = useCallback((value: string) => {
    setQuery((prev) => ({
      ...prev,
      q: value,
      lead_offset: 0,
      case_offset: 0,
    }));
  }, []);

  const setVisaType = useCallback((value: string) => {
    setQuery((prev) => ({
      ...prev,
      visa_type: value,
      lead_offset: 0,
      case_offset: 0,
    }));
  }, []);

  const toggleIncludeClosedCases = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      include_closed_cases: !prev.include_closed_cases,
      case_offset: 0,
    }));
  }, []);

  const nextLeadPage = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      lead_offset: prev.lead_offset + prev.lead_limit,
    }));
  }, []);

  const prevLeadPage = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      lead_offset: Math.max(0, prev.lead_offset - prev.lead_limit),
    }));
  }, []);

  const nextCasePage = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      case_offset: prev.case_offset + prev.case_limit,
    }));
  }, []);

  const prevCasePage = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      case_offset: Math.max(0, prev.case_offset - prev.case_limit),
    }));
  }, []);

  return {
    caseload,
    query,
    loading,
    error,
    refreshing,
    setSearch,
    setVisaType,
    toggleIncludeClosedCases,
    nextLeadPage,
    prevLeadPage,
    nextCasePage,
    prevCasePage,
    refresh,
  };
};
